import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
// Shadcn Select removed - using custom accordion filter
import { Phone, MapPin, Heart, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Home as HomeIcon, Map, List, Globe, Menu, X, Info, Mail, FileText, Search, Calendar, Store, Leaf, PartyPopper } from 'lucide-react';
import { WhatsappShareButton, WhatsappIcon } from 'react-share';
import axios from 'axios';
import { Footer } from '../components/shared/Footer';
import { MapContainer, TileLayer, Marker, Popup, useMap, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TruncatedText, renderFormattedText } from '../components/shared/TextUtils';
import { COMMUNE_MAP, getCommuneFromAddress, getCommune, getRegion } from '../components/shared/LocationUtils';
import { RestaurantCard } from '../components/home/RestaurantCard';
import { getSaintForDate } from '../components/shared/Saints';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// V3 - Fond chaleureux crème
const V3_BG_COLOR = "#FFF8F0";

const customIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// User location marker (blue)
const userIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to recenter map when user location changes
const RecenterMap = ({ userLocation }) => {
  const map = useMap();
  useEffect(() => {
    if (userLocation) {
      map.setView([userLocation.lat, userLocation.lng], 14);
    }
  }, [userLocation, map]);
  return null;
};

export const Home = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [dishes, setDishes] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [filteredDishes, setFilteredDishes] = useState([]);
  const [sortBy, setSortBy] = useState('distance');
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [weekDays, setWeekDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [showMap, setShowMap] = useState(false);
  const [selectedCommune, setSelectedCommune] = useState('all');
  const [locationDenied, setLocationDenied] = useState(false);
  const [nextDayHasDishes, setNextDayHasDishes] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(null); // 'apropos', 'asavoir', 'contact' ou null
  const [events, setEvents] = useState([]);
  const [visibleCount, setVisibleCount] = useState(20);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSection, setFilterSection] = useState(null); // 'lieu' ou 'restaurant'
  const [roadDistances, setRoadDistances] = useState({}); // distances par la route (OSRM)
  const [activeSpecialPages, setActiveSpecialPages] = useState([]); // pages spéciales actives
  const [openCount, setOpenCount] = useState(0); // nombre de restaurants ouverts avec plats
  const [totalCount, setTotalCount] = useState(0); // nombre total de restaurants
  const [showNewRestaurantsPopup, setShowNewRestaurantsPopup] = useState(false);
  const [showAllRestaurants, setShowAllRestaurants] = useState(false); // false = Ouverts, true = Tous
  const [weather, setWeather] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastUpdatedRestaurants, setLastUpdatedRestaurants] = useState([]);

  // Lire les paramètres URL au chargement et réinitialiser si nécessaire
  // Mais ne pas réinitialiser si on change juste la date
  const previousDateRef = useRef(null);
  
  useEffect(() => {
    const sortParam = searchParams.get('sort');
    const communeParam = searchParams.get('commune');
    const dateParam = searchParams.get('date');
    
    // Si seule la date a changé, ne pas toucher aux filtres
    if (previousDateRef.current && dateParam && previousDateRef.current !== dateParam) {
      previousDateRef.current = dateParam;
      return;
    }
    previousDateRef.current = dateParam;
    
    if (sortParam === 'distance') {
      setSortBy('distance');
      setSelectedCommune('all');
      setSelectedRestaurant('all');
    } else if (sortParam === 'alphabetical') {
      setSortBy('restaurant'); // Tri alphabétique A-Z
      setSelectedCommune('all');
      setSelectedRestaurant('all');
    } else if (communeParam) {
      setSelectedCommune(decodeURIComponent(communeParam));
      // Garder le tri par distance si géoloc dispo, sinon alphabétique
      if (!userLocation) {
        setSortBy('restaurant');
      }
      setSelectedRestaurant('all');
    } else if (!sortParam && !communeParam) {
      // Par défaut : À proximité (seulement si aucun paramètre de filtre)
      setSortBy('distance');
      setSelectedCommune('all');
      setSelectedRestaurant('all');
    }
  }, [searchParams]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButtons(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const scrollToBottom = () => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });

  // Référence pour éviter les demandes multiples de géolocalisation
  const locationRequested = useRef(false);
  const detailsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (detailsRef.current?.open && !detailsRef.current.contains(e.target)) {
        detailsRef.current.open = false;
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);


  useEffect(() => {
    const savedFavorites = localStorage.getItem('restaurantFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
    initializeApp();
  }, []);
  
  // Effet séparé pour gérer la géolocalisation selon les paramètres URL
  useEffect(() => {
    const sortParam = searchParams.get('sort');
    const shouldRequestLocation = sortParam === 'distance' || !sortParam;
    
    if (shouldRequestLocation && navigator.geolocation) {
      // Toujours demander la géoloc quand on revient en mode "À proximité"
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationDenied(false);
        },
        (error) => {
          console.log('Géolocalisation refusée ou indisponible:', error.message);
          setLocationDenied(true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    } else if (!navigator.geolocation) {
      setLocationDenied(true);
    }
  }, [searchParams]);

  // Demande la géolocalisation (pour les actions utilisateur)
  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationDenied(false);
        },
        (error) => {
          console.log('Géolocalisation refusée ou indisponible:', error.message);
          setLocationDenied(true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }
  };

  // Demander la localisation quand on ouvre la carte
  useEffect(() => {
    if (showMap) {
      requestLocation();
    }
  }, [showMap]);

  // Rafraîchissement automatique des plats toutes les 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedDate) {
        console.log('Rafraîchissement automatique des plats...');
        loadDishes(selectedDate);
      }
    }, 2 * 60 * 1000); // 2 minutes
    
    return () => clearInterval(interval);
  }, [selectedDate]);

  // Formate une date en YYYY-MM-DD en utilisant l'heure locale (pas UTC)
  const getLocalDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Générer les 21 prochains jours à partir d'aujourd'hui
  const getAvailableDays = () => {
    const now = new Date();
    let startDate;
    if (now.getHours() > 13 || (now.getHours() === 13 && now.getMinutes() >= 30)) {
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() + 1);
    } else {
      startDate = now;
    }
    
    const days = [];
    for (let i = 0; i < 21; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(getLocalDateStr(date));
    }
    return days;
  };

  const initializeApp = async () => {
    const urlDate = searchParams.get('date');
    let displayDate;
    
    const now = new Date();
    let minDate;
    if (now.getHours() > 13 || (now.getHours() === 13 && now.getMinutes() >= 30)) {
      minDate = new Date(now);
      minDate.setDate(minDate.getDate() + 1);
    } else {
      minDate = now;
    }
    
    if (urlDate) {
      const urlDateObj = new Date(urlDate + 'T12:00:00');
      // Vérifier que la date n'est pas dans le passé
      if (urlDateObj >= minDate) {
        displayDate = urlDateObj;
      } else {
        displayDate = minDate;
      }
    } else {
      displayDate = minDate;
    }
    
    const displayDateStr = getLocalDateStr(displayDate);
    setSelectedDate(displayDateStr);

    const days = getAvailableDays();
    setWeekDays(days);
    setCurrentDayIndex(days.indexOf(displayDateStr));

    try {
      const [restaurantsRes, dishesRes] = await Promise.all([
        axios.get(`${API}/restaurants`),
        axios.get(`${API}/dishes?date=${displayDateStr}`)
      ]);
      
      setRestaurants(restaurantsRes.data.map(r => ({ ...r, id: r._id })));
      setDishes(dishesRes.data.map(d => ({
        ...d,
        id: d._id,
        restaurant: d.restaurant ? { ...d.restaurant, id: d.restaurant._id } : null
      })));

      // Charger les événements à venir
      const eventsRes = await axios.get(`${API}/events?upcoming=true`);
      setEvents(eventsRes.data);

      // Charger les pages spéciales actives (pour l'icône flottante)
      try {
        const spRes = await axios.get(`${API}/special-pages?active_only=true`);
        setActiveSpecialPages(spRes.data.filter(p => p.is_active));
      } catch (e) { /* ignore */ }

      // Charger la dernière mise à jour
      try {
        const luRes = await axios.get(`${API}/dishes/last-updated`);
        setLastUpdated(luRes.data.last_updated);
      } catch (e) { /* ignore */ }

      // Charger les derniers restaurants mis à jour
      try {
        const lurRes = await axios.get(`${API}/dishes/last-updated-restaurants?limit=5`);
        setLastUpdatedRestaurants(lurRes.data);
      } catch (e) { /* ignore */ }

      // Charger la météo (Open-Meteo, Genève-Lausanne)
      try {
        const getWeatherIcon = (code) => {
          if (code === 0) return '☀️';
          if (code <= 3) return '⛅';
          if (code <= 48) return '🌫️';
          if (code <= 57) return '🌦️';
          if (code <= 67) return '🌧️';
          if (code <= 77) return '🌨️';
          if (code <= 82) return '🌧️';
          if (code <= 86) return '🌨️';
          if (code >= 95) return '⛈️';
          return '☁️';
        };
        const selDate = new Date(displayDateStr + 'T12:00:00');
        const todayDate = new Date(); todayDate.setHours(0,0,0,0);
        const isToday = selDate.toDateString() === todayDate.toDateString();
        if (isToday) {
          const wRes = await axios.get('https://api.open-meteo.com/v1/forecast?latitude=46.2&longitude=6.15&current_weather=true&timezone=Europe/Zurich');
          const cw = wRes.data?.current_weather;
          if (cw) setWeather({ temp: Math.round(cw.temperature), icon: getWeatherIcon(cw.weathercode), label: '' });
        } else {
          const wRes = await axios.get(`https://api.open-meteo.com/v1/forecast?latitude=46.2&longitude=6.15&daily=temperature_2m_max,weathercode&timezone=Europe/Zurich&start_date=${displayDateStr}&end_date=${displayDateStr}`);
          const data = wRes.data?.daily;
          if (data) setWeather({ temp: Math.round(data.temperature_2m_max[0]), icon: getWeatherIcon(data.weathercode[0]), label: 'prévu' });
        }
      } catch (e) { /* ignore */ }
      
      // Vérifier si le jour suivant a des plats ou événements
      const currentIndex = days.indexOf(displayDateStr);
      if (currentIndex + 1 < days.length) {
        const hasAnyFuture = await (async () => {
          for (let i = currentIndex + 1; i < days.length; i++) {
            const d = days[i];
            const hasEvent = eventsRes.data.some(e => {
              const endDate = e.end_date || e.date;
              return d >= e.date && d <= endDate;
            });
            if (hasEvent) return true;
            try {
              const r = await axios.get(`${API}/dishes?date=${d}`);
              if (r.data && r.data.length > 0) return true;
            } catch (e) { console.error('Weather check failed:', e); }
          }
          return false;
        })();
        setNextDayHasDishes(hasAnyFuture);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDishes = async (date) => {
    try {
      const res = await axios.get(`${API}/dishes?date=${date}`);
      setDishes(res.data.map(d => ({
        ...d,
        id: d._id,
        restaurant: d.restaurant ? { ...d.restaurant, id: d.restaurant._id } : null
      })));
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Vérifier si une date a des plats OU un événement
  const checkDishesForDate = async (dateStr) => {
    try {
      const res = await axios.get(`${API}/dishes?date=${dateStr}`);
      if (res.data && res.data.length > 0) return true;
      // Vérifier aussi les événements
      const hasEvent = events.some(e => {
        const endDate = e.end_date || e.date;
        return dateStr >= e.date && dateStr <= endDate;
      });
      return hasEvent;
    } catch (error) {
      return false;
    }
  };

  // Vérifier s'il reste des jours avec des plats/événements après l'index donné
  const checkAnyFutureDishes = async (fromIndex) => {
    for (let i = fromIndex + 1; i < weekDays.length; i++) {
      const dateStr = weekDays[i];
      // Vérifier les événements d'abord (pas d'appel API)
      const hasEvent = events.some(e => {
        const endDate = e.end_date || e.date;
        return dateStr >= e.date && dateStr <= endDate;
      });
      if (hasEvent) return true;
      // Vérifier les plats
      try {
        const res = await axios.get(`${API}/dishes?date=${dateStr}`);
        if (res.data && res.data.length > 0) return true;
      } catch (e) { /* continue */ }
    }
    return false;
  };

  const changeDay = async (newIndex, scrollToTop = false) => {
    if (newIndex >= 0 && newIndex < weekDays.length) {
      const newDate = weekDays[newIndex];
      
      setCurrentDayIndex(newIndex);
      setSelectedDate(newDate);
      loadDishes(newDate);
      
      // Conserver le filtre actuel dans l'URL
      const params = new URLSearchParams();
      params.set('date', newDate);
      if (selectedCommune && selectedCommune !== 'all') {
        params.set('commune', selectedCommune);
      } else if (sortBy === 'distance') {
        params.set('sort', 'distance');
      } else if (sortBy === 'restaurant') {
        params.set('sort', 'alphabetical');
      }
      navigate(`/plats?${params.toString()}`);
      
      // Scroll en haut seulement si demandé (flèche gauche)
      if (scrollToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
      // Vérifier si des jours futurs ont des plats ou événements
      if (newIndex + 1 < weekDays.length) {
        // Vérifier d'abord si un événement existe dans les jours futurs (rapide, pas d'API)
        const hasAnyFutureEvent = weekDays.slice(newIndex + 1).some(d => 
          events.some(e => {
            const endDate = e.end_date || e.date;
            return d >= e.date && d <= endDate;
          })
        );
        if (hasAnyFutureEvent) {
          setNextDayHasDishes(true);
        } else {
          // Sinon vérifier le jour suivant pour les plats
          const nextDate = weekDays[newIndex + 1];
          const hasDishes = await checkDishesForDate(nextDate);
          setNextDayHasDishes(hasDishes);
        }
      } else {
        setNextDayHasDishes(false);
      }
    }
  };

  // Fonction pour revenir à aujourd'hui/demain (avec scroll en haut)
  const goToToday = async () => {
    const now = new Date();
    let targetDate;
    if (now.getHours() > 13 || (now.getHours() === 13 && now.getMinutes() >= 30)) {
      targetDate = new Date(now);
      targetDate.setDate(targetDate.getDate() + 1);
    } else {
      targetDate = now;
    }
    const targetDateStr = getLocalDateStr(targetDate);
    const targetIndex = weekDays.indexOf(targetDateStr);
    
    if (targetIndex >= 0) {
      await changeDay(targetIndex, true); // Scroll en haut
    }
  };

  // Calculer la date minimum (aujourd'hui ou demain)
  const getMinDateIndex = () => {
    const now = new Date();
    let minDate;
    if (now.getHours() > 13 || (now.getHours() === 13 && now.getMinutes() >= 30)) {
      minDate = new Date(now);
      minDate.setDate(minDate.getDate() + 1);
    } else {
      minDate = now;
    }
    const minDateStr = getLocalDateStr(minDate);
    return weekDays.indexOf(minDateStr);
  };

  const toggleFavorite = (restaurantId) => {
    const newFavorites = favorites.includes(restaurantId)
      ? favorites.filter(id => id !== restaurantId)
      : [...favorites, restaurantId];
    setFavorites(newFavorites);
    localStorage.setItem('restaurantFavorites', JSON.stringify(newFavorites));
  };

  // Fonction de calcul de distance à vol d'oiseau (fallback)
  const calcDistHaversine = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) ** 2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  // Utilise la distance route (OSRM) si dispo, sinon haversine
  const calcDist = (lat1, lng1, lat2, lng2, restaurantId) => {
    if (restaurantId && roadDistances[restaurantId] !== undefined) {
      return roadDistances[restaurantId];
    }
    return calcDistHaversine(lat1, lng1, lat2, lng2);
  };

  const formatDist = (lat1, lng1, lat2, lng2, restaurantId) => {
    const d = calcDist(lat1, lng1, lat2, lng2, restaurantId);
    return d === Infinity ? '?' : d.toFixed(1);
  };

  // Charger les distances par la route via OSRM (un seul appel pour tous les restaurants)
  useEffect(() => {
    if (!userLocation || restaurants.length === 0) return;
    const validRestaurants = restaurants.filter(r => r.lat && r.lng);
    if (validRestaurants.length === 0) return;

    const coords = [`${userLocation.lng},${userLocation.lat}`, ...validRestaurants.map(r => `${r.lng},${r.lat}`)].join(';');
    const url = `https://router.project-osrm.org/table/v1/driving/${coords}?sources=0&annotations=distance`;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (data.code === 'Ok' && data.distances && data.distances[0]) {
          const distances = {};
          validRestaurants.forEach((r, i) => {
            const meters = data.distances[0][i + 1];
            distances[r.id] = meters !== null ? meters / 1000 : Infinity;
          });
          setRoadDistances(distances);
        }
      })
      .catch(() => {
        // Fallback silencieux sur haversine
      });
  }, [userLocation, restaurants]);

  useEffect(() => {
    let result = [...dishes];

    // Exclure les plats "Fermé" (marqueur de fermeture admin)
    const closedRestaurantIds = new Set(
      result.filter(d => d.name === 'Fermé').map(d => d.restaurant_id)
    );
    result = result.filter(d => d.name !== 'Fermé');

    // Filtrer par région/commune si sélectionnée
    if (selectedCommune !== 'all') {
      const restaurantIdsInRegion = restaurants
        .filter(r => getRegion(r) === selectedCommune)
        .map(r => r.id);
      result = result.filter(dish => restaurantIdsInRegion.includes(dish.restaurant_id));
    }

    if (selectedRestaurant !== 'all') {
      result = result.filter(dish => dish.restaurant_id === selectedRestaurant);
    }

    if (showOnlyFavorites && favorites.length > 0) {
      result = result.filter(dish => favorites.includes(dish.restaurant_id));
    }

    const grouped = {};
    result.forEach(dish => {
      const rid = dish.restaurant_id;
      if (!grouped[rid]) {
        grouped[rid] = { restaurant: dish.restaurant, dishes: [], isClosed: false };
      }
      grouped[rid].dishes.push(dish);
    });

    // Tri par restaurant : plats du jour en premier, puis suggestions/valid_until
    // + dédoublonnage par nom (si un plat du jour a le même nom qu'un hérité, on garde le plat du jour)
    Object.values(grouped).forEach(group => {
      const seen = new Set();
      const daily = [];
      const suggestions = [];
      group.dishes.forEach(dish => {
        if (dish.is_suggestion || (dish.valid_until && dish.date !== selectedDate)) {
          suggestions.push(dish);
        } else {
          daily.push(dish);
          seen.add(dish.name.toLowerCase());
        }
      });
      // Ne garder que les suggestions dont le nom n'est pas déjà dans les plats du jour
      const uniqueSuggestions = suggestions.filter(d => !seen.has(d.name.toLowerCase()));
      // Trier les plats du jour : sans valid_until en premier, puis avec valid_until
      daily.sort((a, b) => {
        if (!a.valid_until && b.valid_until) return -1;
        if (a.valid_until && !b.valid_until) return 1;
        return 0;
      });
      group.dishes = [...daily, ...uniqueSuggestions];
    });

    // Intégrer les événements du jour sélectionné comme plats du jour
    events.forEach(event => {
      const eventDate = event.date;
      const eventEndDate = event.end_date || event.date;
      // L'événement couvre-t-il le jour sélectionné ?
      if (selectedDate >= eventDate && selectedDate <= eventEndDate) {
        const rid = event.restaurant_id;
        const restaurant = restaurants.find(r => r.id === rid);
        if (!restaurant) return;
        // Créer un plat virtuel à partir de l'événement
        const eventDish = {
          id: `event-${event._id || event.id}`,
          name: event.title,
          description: event.menu || event.description || '',
          price: event.price || null,
          restaurant_id: rid,
          restaurant: restaurant,
          is_suggestion: false,
          is_event: true,
          is_title_centered: event.is_title_centered || false,
          is_description_centered: event.is_menu_centered || false,
          date: selectedDate
        };
        if (!grouped[rid]) {
          grouped[rid] = { restaurant: restaurant, dishes: [eventDish], isClosed: false };
        } else {
          // Ajouter en premier (l'événement est prioritaire)
          grouped[rid].dishes.unshift(eventDish);
          grouped[rid].noDishesRegistered = false;
        }
      }
    });

    // Ajouter les restaurants fermés ce jour-là (qui n'ont pas de plats)
    // Calculer quel jour de la semaine est sélectionné (0=Lundi, ..., 6=Dimanche)
    const selectedDateObj = new Date(selectedDate + 'T12:00:00');
    const dayOfWeek = selectedDateObj.getDay(); // 0=Dimanche, 1=Lundi, ..., 6=Samedi
    const closedDayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convertir en 0=Lundi, ..., 6=Dimanche
    
    // Nom du jour en français
    const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const dayNameFr = dayNames[dayOfWeek];

    // Filtrer les restaurants selon les critères actuels
    let restaurantsToCheck = restaurants;
    if (selectedCommune !== 'all') {
      restaurantsToCheck = restaurantsToCheck.filter(r => getRegion(r) === selectedCommune);
    }
    if (selectedRestaurant !== 'all') {
      restaurantsToCheck = restaurantsToCheck.filter(r => r.id === selectedRestaurant);
    }
    if (showOnlyFavorites && favorites.length > 0) {
      restaurantsToCheck = restaurantsToCheck.filter(r => favorites.includes(r.id));
    }

    // Fonction pour vérifier si le restaurant est en vacances pour la date sélectionnée
    const isOnVacation = (restaurant) => {
      if (!restaurant?.vacation_start || !restaurant?.vacation_end) return false;
      const today = new Date(selectedDate);
      const start = new Date(restaurant.vacation_start);
      const end = new Date(restaurant.vacation_end);
      return today >= start && today <= end;
    };

    // Ajouter les restaurants fermés ce jour-là (jour de fermeture, temporaire ou vacances)
    restaurantsToCheck.forEach(restaurant => {
      if (!grouped[restaurant.id]) {
        // Vérifier fermeture temporaire
        if (restaurant.temporary_closure) {
          grouped[restaurant.id] = { 
            restaurant: restaurant, 
            dishes: [], 
            isTemporaryClosure: true
          };
        }
        // Vérifier vacances
        else if (isOnVacation(restaurant)) {
          grouped[restaurant.id] = { 
            restaurant: restaurant, 
            dishes: [], 
            isOnVacation: true,
            vacationStart: restaurant.vacation_start,
            vacationEnd: restaurant.vacation_end,
            vacationReason: restaurant.vacation_reason
          };
        }
        // Vérifier jour de fermeture habituel
        else if (restaurant.closed_days?.includes(closedDayIndex)) {
          grouped[restaurant.id] = { 
            restaurant: restaurant, 
            dishes: [], 
            isClosed: true,
            closedDayName: dayNameFr
          };
        }
        // Restaurant ouvert mais sans plats enregistrés
        else {
          grouped[restaurant.id] = { 
            restaurant: restaurant, 
            dishes: [], 
            noDishesRegistered: true
          };
        }
      }
    });

    // Marquer aussi les restaurants existants comme fermés si nécessaire
    Object.keys(grouped).forEach(rid => {
      const restaurant = grouped[rid].restaurant;
      // Fermeture temporaire prioritaire
      if (restaurant?.temporary_closure) {
        grouped[rid].isTemporaryClosure = true;
        grouped[rid].isClosed = false;
        grouped[rid].isOnVacation = false;
        grouped[rid].noDishesRegistered = false;
      }
      // Puis vacances
      else if (isOnVacation(restaurant)) {
        grouped[rid].isOnVacation = true;
        grouped[rid].vacationStart = restaurant.vacation_start;
        grouped[rid].vacationEnd = restaurant.vacation_end;
        grouped[rid].vacationReason = restaurant.vacation_reason;
        grouped[rid].isClosed = false;
        grouped[rid].noDishesRegistered = false;
      }
      // Puis jour de fermeture habituel
      else if (restaurant?.closed_days?.includes(closedDayIndex)) {
        grouped[rid].isClosed = true;
        grouped[rid].closedDayName = dayNameFr;
        grouped[rid].noDishesRegistered = false;
      }
    });

    // Marquer les restaurants avec un plat "Fermé" comme fermés (fermeture exceptionnelle)
    closedRestaurantIds.forEach(rid => {
      if (grouped[rid]) {
        grouped[rid].isClosed = true;
        grouped[rid].closedDayName = null; // Pas de jour = juste "Fermé"
        grouped[rid].dishes = [];
        grouped[rid].noDishesRegistered = false;
      }
    });

    let arr = Object.values(grouped);

    // Appliquer le filtre de recherche sur le résultat final
    const searchLower = searchQuery.toLowerCase().trim();
    if (searchLower) {
      arr = arr.filter(group => {
        // Chercher dans le nom du restaurant
        if (group.restaurant?.name?.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Chercher dans les noms ET descriptions des plats
        if (group.dishes.some(dish => 
          dish.name?.toLowerCase().includes(searchLower) ||
          dish.description?.toLowerCase().includes(searchLower)
        )) {
          return true;
        }
        return false;
      });
    }

    // Fonction de tri par distance
    const sortByDistance = (items) => {
      if (userLocation) {
        return items.sort((a, b) => {
          const distA = calcDist(userLocation.lat, userLocation.lng, a.restaurant?.lat, a.restaurant?.lng, a.restaurant?.id);
          const distB = calcDist(userLocation.lat, userLocation.lng, b.restaurant?.lat, b.restaurant?.lng, b.restaurant?.id);
          return distA - distB;
        });
      } else {
        return items.sort((a, b) => a.restaurant?.name?.localeCompare(b.restaurant?.name));
      }
    };

    if (sortBy === 'restaurant') {
      // Tri alphabétique
      arr.forEach(g => { g.isSecondarySection = false; });
      arr.sort((a, b) => a.restaurant?.name?.localeCompare(b.restaurant?.name));
      
      // Compter ouverts (avec plats ou sans) vs fermés
      const openArr = arr.filter(g => !g.isClosed && !g.isTemporaryClosure && !g.isOnVacation);
      const closedArr = arr.filter(g => g.isClosed || g.isTemporaryClosure || g.isOnVacation);
      setOpenCount(openArr.length);
      setTotalCount(arr.length);
      
      if (showAllRestaurants) {
        // Tous mélangés en A-Z, pas de séparation
        setFilteredDishes(arr);
      } else {
        // Ouverts seulement : avec plats en premier (A-Z), sans plats à la fin (A-Z)
        const withDishes = openArr.filter(g => g.dishes.length > 0);
        const noDishes = openArr.filter(g => g.dishes.length === 0);
        setFilteredDishes([...withDishes, ...noDishes]);
      }
    } else {
      // Tri par distance (proximité ou commune avec localisation)
      arr.forEach(g => { g.isSecondarySection = false; });
      
      // Séparer en groupes
      const openWithDishes = arr.filter(g => 
        !g.isClosed && !g.isTemporaryClosure && !g.isOnVacation && 
        g.dishes.some(d => !d.is_suggestion)
      );
      const openSuggestionsOnly = arr.filter(g => 
        !g.isClosed && !g.isTemporaryClosure && !g.isOnVacation && 
        g.dishes.length > 0 && !g.dishes.some(d => !d.is_suggestion)
      );
      const closed = arr.filter(g => 
        g.isClosed || g.isTemporaryClosure || g.isOnVacation
      );
      const noDishes = arr.filter(g => 
        !g.isClosed && !g.isTemporaryClosure && !g.isOnVacation && 
        g.dishes.length === 0
      );
      
      // Trier chaque groupe par distance
      sortByDistance(openWithDishes);
      sortByDistance(openSuggestionsOnly);
      sortByDistance(closed);
      sortByDistance(noDishes);
      
      const withDishes = [...openWithDishes, ...openSuggestionsOnly];
      sortByDistance(withDishes);
      
      setOpenCount(withDishes.length + noDishes.length);
      setTotalCount(withDishes.length + closed.length + noDishes.length);
      
      if (showAllRestaurants) {
        // Tous mélangés par distance, pas de séparation
        const all = [...withDishes, ...noDishes, ...closed];
        sortByDistance(all);
        all.forEach(g => { g.isSecondarySection = false; });
        setFilteredDishes(all);
      } else {
        // Ouverts : avec plats par distance, puis sans plats à la fin par distance
        [...withDishes, ...noDishes].forEach(g => { g.isSecondarySection = false; });
        setFilteredDishes([...withDishes, ...noDishes]);
      }
    }
  }, [dishes, sortBy, selectedRestaurant, selectedCommune, showOnlyFavorites, favorites, userLocation, restaurants, selectedDate, searchQuery, roadDistances, showAllRestaurants, events]);

  const formatFullDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const getDateLabel = () => {
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const sel = new Date(selectedDate); sel.setHours(0,0,0,0);
    if (sel.getTime() === today.getTime()) return "aujourd'hui";
    if (sel.getTime() === tomorrow.getTime()) return "demain";
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F0]">
        <div className="text-center">
          <div className="animate-pulse mb-6">
            <h1 className="text-4xl font-bold text-warmgray-dark">
              AUMENU<span className="text-terracotta">.CH</span>
            </h1>
          </div>
          <div className="flex justify-center gap-2">
            <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
          </div>
        </div>
      </div>
    );
  }

  const dateLabel = getDateLabel();

  // Calculer les nouveaux restaurants (marqués show_as_new ET créés il y a moins de 3 jours)
  const newRestaurants = restaurants.filter(r => {
    if (!r.show_as_new) return false;
    if (!r.created_at) return false;
    const created = new Date(r.created_at);
    const now = new Date();
    return Math.floor((now - created) / (1000 * 60 * 60 * 24)) <= 3;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="min-h-screen relative" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
      
      {/* Background ardoise */}
      <div 
        className="fixed inset-0 z-0"
        style={{ 
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_cef8a789-684e-42da-8da6-08c3769b942a/artifacts/02czd85l_blackboard%20texture%200102.jpg)`, 
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      ></div>
      

      {/* Badge Pâques - fixe sur le fond gris */}
      {activeSpecialPages.length > 0 && !showMap && activeSpecialPages.map(sp => (
        <Link
          key={sp._id || sp.id}
          to={`/fete/${sp.slug}`}
          className="fixed top-[235px] sm:top-[245px] left-1/2 -translate-x-1/2 z-[35]"
          data-testid={`easter-floating-btn-${sp.slug}`}
        >
          <div className="flex items-center gap-2.5 bg-black/60 backdrop-blur-sm rounded-full pl-1.5 pr-5 py-1.5 border border-[#D4AF37]/60 shadow-2xl animate-bounce-slow hover:scale-105 transition-transform duration-300 cursor-pointer">
            <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border border-[#D4AF37]/40">
              <img src="/lapin-paques.png" alt="" className="w-full h-full object-cover" />
            </div>
            <span className="text-white text-xs sm:text-sm font-semibold whitespace-nowrap">
              Découvrez les Menus de Pâques <span className="text-[#D4AF37]">&rarr;</span>
            </span>
          </div>
        </Link>
      ))}

      {/* Header */}
      {/* Header - Sticky avec fond ardoise */}
      <div className="sticky top-0 z-[40]">
        <header 
          className="shadow-md"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_cef8a789-684e-42da-8da6-08c3769b942a/artifacts/02czd85l_blackboard%20texture%200102.jpg)`, 
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="container mx-auto px-4 py-3 grid grid-cols-[1fr_auto_1fr] items-center">
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2 text-[#E8DFD5] hover:text-[#D4AF37]">
                <HomeIcon className="h-5 w-5" />
                <span className="text-sm hidden sm:inline">Accueil</span>
              </Link>
            </div>
            <h1 className="text-2xl font-bold text-[#E8DFD5] whitespace-nowrap text-center" data-testid="header-logo">
              AUMENU<span className="text-[#D4AF37]">.CH</span>
            </h1>
            <div className="flex items-center justify-end gap-2">
              <button 
                onClick={() => setMenuOpen(!menuOpen)}
                className="text-[#E8DFD5] hover:text-[#D4AF37] transition-colors p-1"
                data-testid="header-menu-button"
              >
                {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </header>

        {/* Menu déroulant - Simple */}
        {menuOpen && (
          <>
            {/* Overlay pour fermer en cliquant dehors */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setMenuOpen(false)}
            ></div>
            <div className="absolute right-4 top-14 bg-black/95 border border-[#D4AF37]/40 rounded-lg shadow-xl z-50 min-w-48 overflow-hidden">
              <button 
                onClick={() => { setSearchOpen(!searchOpen); if (searchOpen) setSearchQuery(''); setMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-4 text-[#D4AF37] font-medium hover:bg-[#D4AF37]/10 transition-colors border-b border-[#D4AF37]/20"
              >
                <Search className="h-4 w-4" /> Rechercher
              </button>
              <button 
                onClick={() => { setShowMap(!showMap); setMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-4 text-[#D4AF37] font-medium hover:bg-[#D4AF37]/10 transition-colors border-b border-[#D4AF37]/20"
              >
                <Map className="h-4 w-4" /> {showMap ? 'Voir la liste' : 'Voir la carte'}
              </button>
              <button 
                onClick={() => { setModalOpen('apropos'); setMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-4 text-[#D4AF37] font-medium hover:bg-[#D4AF37]/10 transition-colors border-b border-[#D4AF37]/20"
              >
                <Info className="h-4 w-4" /> À propos
              </button>
              <button 
                onClick={() => { setModalOpen('asavoir'); setMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-4 text-[#D4AF37] font-medium hover:bg-[#D4AF37]/10 transition-colors border-b border-[#D4AF37]/20"
              >
                <Info className="h-4 w-4" /> À savoir
              </button>
              <button 
                onClick={() => { setModalOpen('contact'); setMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-4 text-[#D4AF37] font-medium hover:bg-[#D4AF37]/10 transition-colors border-b border-[#D4AF37]/20"
              >
                <Mail className="h-4 w-4" /> Contact
              </button>
              <Link 
                to="/confidentialite" 
                className="flex items-center gap-3 p-4 text-[#D4AF37] font-medium hover:bg-[#D4AF37]/10 transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                <FileText className="h-4 w-4" /> Confidentialité
              </Link>
            </div>
          </>
        )}

        {/* Modales */}
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Overlay sombre */}
            <div 
              className="absolute inset-0 bg-black/70" 
              onClick={() => setModalOpen(null)}
            ></div>
            
            {/* Contenu de la modale */}
            <div className="relative bg-black/95 border border-[#D4AF37]/40 rounded-lg shadow-xl max-w-md w-full p-6 z-10">
              {/* Bouton fermer */}
              <button 
                onClick={() => setModalOpen(null)}
                className="absolute top-3 right-3 text-[#E8DFD5]/60 hover:text-[#D4AF37] transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              
              {/* À propos */}
              {modalOpen === 'apropos' && (
                <>
                  <h3 className="text-[#D4AF37] font-bold text-lg mb-4 flex items-center gap-2">
                    <Info className="h-5 w-5" /> À propos
                  </h3>
                  <div className="text-[#E8DFD5]/90 text-sm leading-relaxed">
                    <p>Midi approche... et la question revient : <em>"On mange quoi ?"</em></p>
                    <p className="mt-3"><strong>AuMenu.ch</strong> est né de cette question quotidienne. Notre plateforme réunit les plats du jour des restaurants de Versoix & région + la Côte pour vous simplifier la vie.</p>
                    <p className="text-[#E8DFD5]/70 mt-3 italic">Local, pratique, gourmand. Bon appétit !</p>
                  </div>
                </>
              )}
              
              {/* À savoir */}
              {modalOpen === 'asavoir' && (
                <>
                  <h3 className="text-[#D4AF37] font-bold text-lg mb-4 flex items-center gap-2">
                    <Info className="h-5 w-5" /> À savoir
                  </h3>
                  <ul className="text-[#E8DFD5]/80 text-sm space-y-2">
                    <li>• Plats servis généralement de 12h à 14h</li>
                    <li>• Réservation conseillée directement auprès du restaurant</li>
                    <li>• Les plats sont susceptibles d'être modifiés en tout temps par les Restaurateurs</li>
                  </ul>
                </>
              )}
              
              {/* Contact */}
              {modalOpen === 'contact' && (
                <>
                  <h3 className="text-[#D4AF37] font-bold text-lg mb-4 flex items-center gap-2">
                    <Mail className="h-5 w-5" /> Contact
                  </h3>
                  <div className="text-[#E8DFD5]/80 text-sm">
                    <p className="mb-3">
                      N'hésitez pas à nous contacter pour inscrire votre restaurant ou nous faire part de vos remarques sur le site.
                    </p>
                    <p className="text-[#E8DFD5]/60 text-xs italic mb-4">
                      (Pour toute question concernant les menus, veuillez contacter directement les restaurants.)
                    </p>
                    <a 
                      href="mailto:info@aumenu.ch" 
                      className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E8DFD5] transition-colors text-base"
                    >
                      📧 info@aumenu.ch
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ====== ZONE TITRE — Sur fond ardoise ====== */}
        <div 
          className="py-2 px-4 pb-12"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_cef8a789-684e-42da-8da6-08c3769b942a/artifacts/02czd85l_blackboard%20texture%200102.jpg)`, 
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="container mx-auto">
            
            {/* Titre animé */}
            <p className="text-base sm:text-lg text-center text-[#E8DFD5] font-medium mb-3" style={{ animation: 'homeSlideIn 0.5s ease-out both' }}>
              {dateLabel === "aujourd'hui" 
                ? <>Aujourd'hui</>
                : dateLabel === "demain" 
                  ? <>Demain à midi</>
                  : <>À midi</>
              }
            </p>
            
            {/* Date avec flèches — texte blanc sans capsule */}
            <div className="flex items-center justify-center gap-3 mb-3">
              <button onClick={goToToday} disabled={currentDayIndex === getMinDateIndex()}
                className={`p-2 rounded-full ${currentDayIndex === getMinDateIndex() ? 'text-white/30 cursor-not-allowed' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-[clamp(1.25rem,6vw,1.875rem)] font-bold capitalize text-white whitespace-nowrap">
                {formatFullDate(selectedDate)}
              </span>
              <button onClick={() => changeDay(currentDayIndex + 1)} disabled={!nextDayHasDishes}
                className={`p-2 rounded-full ${!nextDayHasDishes ? 'text-white/30 cursor-not-allowed' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Saint du jour + Météo */}
            <div className="flex items-center justify-center gap-2 text-xs text-white/70" data-testid="saint-weather-line">
              {(() => {
                const selDate = new Date(selectedDate + 'T12:00:00');
                const saint = getSaintForDate(selDate);
                return saint ? <span>⛪ {saint}</span> : null;
              })()}
              {weather && (
                <>
                  <span className="text-white/40">•</span>
                  <span>{weather.icon} {weather.temp}°C {weather.label}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ====== CAPSULE FILTRES — À cheval ardoise/beige ====== */}
        <div 
          className="relative z-20 -mt-10 px-4 pb-6"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_cef8a789-684e-42da-8da6-08c3769b942a/artifacts/02czd85l_blackboard%20texture%200102.jpg)`, 
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div 
            className="mx-auto max-w-md rounded-2xl border border-stone-400 px-3 py-3"
            style={{ 
              background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(240,230,216,0.95) 100%)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            data-testid="filter-capsule"
          >
            <div className="flex gap-2 justify-center items-center flex-wrap">
              <div className="relative">
                <button 
                  onClick={() => { setFilterOpen(!filterOpen); if (!filterOpen) setFilterSection(null); }}
                  className="h-8 text-xs bg-[#D4AF37] text-white font-medium border border-[#C4A030] rounded-full px-4 flex items-center gap-1.5 shadow-sm"
                  data-testid="filter-trigger"
                >
                  <span className="truncate max-w-[120px]">
                    {selectedCommune !== 'all' 
                      ? selectedCommune
                      : sortBy === 'restaurant'
                        ? `A-Z`
                        : '\u00C0 proximit\u00E9'
                    }
                  </span>
                  <ChevronDown className={`h-3 w-3 flex-shrink-0 transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
                </button>

                {filterOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setFilterOpen(false)} />
                    <div className="absolute top-full left-0 mt-1 w-64 sm:w-72 bg-white border border-terracotta/20 rounded-lg shadow-xl z-50 max-h-[60vh] overflow-y-auto" data-testid="filter-dropdown">
                      
                      {/* À proximité */}
                      <button
                        onClick={() => {
                          setSelectedCommune('all');
                          setSelectedRestaurant('all');
                          setSortBy('distance');
                          setVisibleCount(20);
                          setFilterOpen(false);
                          if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(
                              (position) => { setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude }); setLocationDenied(false); },
                              () => setLocationDenied(true),
                              { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
                            );
                          }
                          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-terracotta/10 ${selectedCommune === 'all' && sortBy === 'distance' ? 'bg-terracotta/10 text-terracotta' : 'text-warmgray-dark'}`}
                      >
                        <MapPin className="h-4 w-4 text-terracotta" />
                        {'\u00C0 proximit\u00E9'}
                      </button>

                      <div className="h-px bg-terracotta/10" />

                      {/* Par Lieu - Accordéon */}
                      <button
                        onClick={() => setFilterSection(filterSection === 'lieu' ? null : 'lieu')}
                        className="w-full text-left px-4 py-2.5 text-sm font-semibold flex items-center justify-between hover:bg-terracotta/10 text-warmgray-dark"
                      >
                        <span className="flex items-center gap-2">
                          <Map className="h-4 w-4 text-terracotta" />
                          Par lieu
                        </span>
                        <ChevronDown className={`h-4 w-4 text-terracotta transition-transform ${filterSection === 'lieu' ? 'rotate-180' : ''}`} />
                      </button>
                      {filterSection === 'lieu' && (
                        <div className="bg-stone-50/50">
                          {(() => {
                            const today = selectedDate || new Date().toISOString().split('T')[0];
                            const dayIndex = new Date(today + 'T12:00:00').getDay();
                            const grouped = restaurants.reduce((acc, r) => {
                              const region = getRegion(r);
                              if (!acc[region]) acc[region] = { total: 0, open: 0 };
                              acc[region].total++;
                              // Compter comme "ouvert" si le restaurant a un plat du jour (non-suggestion)
                              const isClosed = r.closed_days?.includes(dayIndex);
                              const isVacation = r.vacation_start && r.vacation_end && today >= r.vacation_start && today <= r.vacation_end;
                              const hasDailyDish = dishes.some(d => d.restaurant_id === (r._id || r.id) && !d.is_suggestion);
                              if (!isClosed && !isVacation && !r.temporary_closure && hasDailyDish) {
                                acc[region].open++;
                              }
                              return acc;
                            }, {});
                            return Object.keys(grouped).sort((a, b) => a.localeCompare(b)).map(region => (
                              <button
                                key={region}
                                onClick={() => {
                                  setSelectedCommune(region);
                                  setSelectedRestaurant('all');
                                  setVisibleCount(20);
                                  if (!userLocation) setSortBy('restaurant');
                                  setFilterOpen(false);
                                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                                }}
                                className={`w-full text-left px-8 py-2 text-sm flex items-center justify-between hover:bg-terracotta/10 ${selectedCommune === region ? 'bg-terracotta/10 text-terracotta font-medium' : 'text-warmgray-dark'}`}
                              >
                                <span>{region}</span>
                                <span className="text-terracotta/60 text-xs">({grouped[region].total})</span>
                              </button>
                            ));
                          })()}
                        </div>
                      )}

                      <div className="h-px bg-terracotta/10" />

                      {/* Par Restaurant - Accordéon */}
                      <button
                        onClick={() => setFilterSection(filterSection === 'restaurant' ? null : 'restaurant')}
                        className="w-full text-left px-4 py-2.5 text-sm font-semibold flex items-center justify-between hover:bg-terracotta/10 text-warmgray-dark"
                      >
                        <span className="flex items-center gap-2">
                          <Store className="h-4 w-4 text-terracotta" />
                          Par restaurant
                        </span>
                        <ChevronDown className={`h-4 w-4 text-terracotta transition-transform ${filterSection === 'restaurant' ? 'rotate-180' : ''}`} />
                      </button>
                      {filterSection === 'restaurant' && (
                        <div className="bg-stone-50/50">
                          <button
                            onClick={() => {
                              setSelectedCommune('all');
                              setSelectedRestaurant('all');
                              setSortBy('restaurant');
                              setFilterOpen(false);
                              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                            }}
                            className={`w-full text-left px-8 py-2 text-sm font-medium hover:bg-terracotta/10 ${sortBy === 'restaurant' && selectedCommune === 'all' ? 'bg-terracotta/10 text-terracotta' : 'text-warmgray-dark'}`}
                          >
                            Restaurants A-Z ({restaurants.length})
                          </button>
                          {restaurants
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(r => (
                              <button
                                key={r.id}
                                onClick={() => {
                                  setFilterOpen(false);
                                  navigate(`/restaurant/${r.slug || r.id}`);
                                }}
                                className="w-full text-left px-8 py-2 text-sm text-warmgray hover:bg-terracotta/10 hover:text-warmgray-dark"
                              >
                                {r.name}
                              </button>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Toggle Tous/Ouverts */}
              <div className="inline-flex h-8 rounded-full border border-[#D4AF37]/50 overflow-hidden" data-testid="restaurant-toggle">
                <button
                  onClick={() => { setShowAllRestaurants(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`px-3 text-xs font-medium transition-all ${
                    showAllRestaurants
                      ? 'bg-[#D4AF37] text-white shadow-sm'
                      : 'bg-transparent text-[#8B6914] hover:text-[#D4AF37]'
                  }`}
                  data-testid="toggle-all"
                >
                  Tous ({totalCount})
                </button>
                <button
                  onClick={() => { setShowAllRestaurants(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`px-3 text-xs font-medium transition-all ${
                    !showAllRestaurants
                      ? 'bg-[#D4AF37] text-white shadow-sm'
                      : 'bg-transparent text-[#8B6914] hover:text-[#D4AF37]'
                  }`}
                  data-testid="toggle-open"
                >
                  Ouverts ({openCount})
                </button>
              </div>

              {/* New badge doré */}
              {newRestaurants.length > 0 && (
                <button
                  onClick={() => setShowNewRestaurantsPopup(true)}
                  className="h-8 text-xs border border-[#D4AF37] rounded-full px-4 bg-[#D4AF37] text-white font-bold shadow-sm hover:bg-[#C4A030] transition-colors new-badge-gold"
                  data-testid="new-restaurants-link"
                >
                  New {newRestaurants.length}
                </button>
              )}

              {/* Favoris */}
              <button 
                onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
                className={`h-8 flex items-center gap-1 px-3 rounded-full border transition-colors ${
                  showOnlyFavorites 
                    ? 'bg-[#D4AF37] text-white border-[#C4A030]' 
                    : 'bg-transparent text-[#8B6914] border-[#D4AF37]/50 hover:bg-[#D4AF37]/10'
                }`}
                data-testid="header-favorites-button"
              >
                <Heart className={`h-4 w-4 ${showOnlyFavorites ? 'fill-current' : ''}`} />
                {favorites.length > 0 && (
                  <span className="text-xs font-medium">{favorites.length}</span>
                )}
              </button>
            </div>

            {/* Barre de recherche */}
            {searchOpen && (
              <div className="mt-2 flex justify-center px-2">
                <div className="relative w-full max-w-xs sm:max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-warmgray/50" />
                  <input
                    type="text"
                    placeholder="Restaurant ou plat..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-8 py-1.5 rounded-full border border-stone-300 bg-white text-warmgray-dark text-base focus:outline-none focus:border-[#D4AF37]"
                    autoFocus
                    style={{ fontSize: '16px' }}
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-warmgray/50 hover:text-warmgray"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {/* Suggestions de recherche */}
                  {searchQuery.length >= 2 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                      {restaurants
                        .filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase()))
                        .slice(0, 6)
                        .map(r => (
                          <button
                            key={r.id}
                            onClick={() => {
                              setSearchQuery('');
                              setSearchOpen(false);
                              navigate(`/restaurant/${r.slug || r.id}`);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm text-warmgray-dark hover:bg-stone-100 flex items-center gap-2 border-b border-stone-100 last:border-0"
                            data-testid={`search-result-${r.id}`}
                          >
                            <ChevronRight className="h-3 w-3 text-terracotta" />
                            {r.name}
                          </button>
                        ))
                      }
                      {restaurants.filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                        <p className="px-4 py-2.5 text-sm text-warmgray italic">Aucun restaurant trouvé</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pop-up géolocalisation */}
            {locationDenied && sortBy === 'distance' && !userLocation && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/40" onClick={requestLocation}></div>
                <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
                  <MapPin className="h-8 w-8 text-[#D4AF37] mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-stone-800 mb-2">Localisation</h3>
                  <p className="text-sm text-stone-600 mb-4">Activez la localisation pour voir les restaurants les plus proches de vous.</p>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => { setLocationDenied(false); setSortBy('restaurant'); }}
                      className="flex-1 px-4 py-2 text-sm rounded-full border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors"
                    >
                      Plus tard
                    </button>
                    <button 
                      onClick={requestLocation}
                      className="flex-1 px-4 py-2 text-sm rounded-full bg-[#D4AF37] text-white font-medium hover:bg-[#C4A030] transition-colors"
                    >
                      Activer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Texte défilant — dans la capsule */}
            <div className="mt-2 pt-2 border-t border-stone-100 overflow-hidden" data-testid="marquee-bar">
              <div className="flex items-center gap-2">
                {/* Partie fixe — màj il y a Xh */}
                <div className="flex items-center gap-1.5 flex-shrink-0 text-xs text-stone-500 pl-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500" style={{ animation: 'livePulse 2s ease-in-out infinite' }}></span>
                  <span className="font-medium">
                    {(() => {
                      if (!lastUpdated) return 'màj...';
                      const d = new Date(lastUpdated);
                      const diffMs = Date.now() - d.getTime();
                      const diffMin = Math.floor(diffMs / 60000);
                      if (diffMin < 1) return "màj à l'instant";
                      if (diffMin < 60) return `màj il y a ${diffMin}min`;
                      const diffH = Math.floor(diffMin / 60);
                      if (diffH < 24) return `màj il y a ${diffH}h`;
                      return `màj il y a ${Math.floor(diffH / 24)}j`;
                    })()}
                  </span>
                </div>
                {/* Séparateur */}
                <div className="w-px h-3 bg-stone-200 flex-shrink-0"></div>
                {/* Partie défilante — derniers restaurants */}
                <div className="flex-1 overflow-hidden">
                  <div className="marquee-track text-xs text-stone-600 whitespace-nowrap">
                    <span className="inline-block marquee-content">
                      {lastUpdatedRestaurants.length > 0 
                        ? lastUpdatedRestaurants.map(r => r.name).join('  ✦  ') + '  ✦  ' + lastUpdatedRestaurants.map(r => r.name).join('  ✦  ')
                        : `${dishes.length} plats du jour en ligne  ✦  ${dishes.length} plats du jour en ligne`
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Content - Map or Menu */}
      {showMap ? (
        /* Map View */
        <main className="container mx-auto px-4 py-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden border-2 border-terracotta/20 shadow-lg relative z-0" style={{height: '55vh', minHeight: '350px'}}>
              <MapContainer 
                center={userLocation ? [userLocation.lat, userLocation.lng] : [46.2833, 6.1667]} 
                zoom={13} 
                style={{height: '100%', width: '100%'}}
                zoomControl={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <RecenterMap userLocation={userLocation} />
                
                {/* User location marker */}
                {userLocation && (
                  <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
                    <Popup>
                      <div className="text-center">
                        <p className="font-semibold text-blue-600">📍 Vous êtes ici</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {restaurants.filter(r => r.lat && r.lng).map(restaurant => {
                  const distance = userLocation ? formatDist(userLocation.lat, userLocation.lng, restaurant.lat, restaurant.lng, restaurant.id) : null;
                  const restaurantDishes = filteredDishes.find(g => g.restaurant?.id === restaurant.id);
                  
                  return (
                    <Marker key={restaurant.id} position={[restaurant.lat, restaurant.lng]} icon={customIcon}>
                      <Tooltip direction="top" offset={[0, -35]} opacity={0.9}>
                        <span className="font-semibold">{restaurant.name}</span>
                      </Tooltip>
                      <Popup>
                        <div className="text-center min-w-[200px]">
                          <h3 className="font-bold text-lg text-amber-700 mb-1">{restaurant.name}</h3>
                          {distance && <p className="text-sm text-gray-500 mb-2">📍 {distance} km</p>}
                          <p className="text-xs text-gray-600 mb-2">{restaurant.address}</p>
                          <a href={`tel:${restaurant.phone}`} className="text-xs text-blue-600 block mb-3">📞 {restaurant.phone}</a>
                          {restaurantDishes && restaurantDishes.dishes.length > 0 && (
                            <div className="border-t pt-2 mb-2">
                              <p className="text-xs font-semibold mb-1">Plats du jour :</p>
                              {restaurantDishes.dishes.slice(0, 2).map(dish => (
                                <p key={dish.id} className="text-xs text-gray-700 flex items-center gap-1">{dish.name}{dish.is_vegetarian && <Leaf className="h-3 w-3 text-green-600 inline" />} - CHF {Number.isInteger(dish.price) ? dish.price + '.-' : dish.price.toFixed(2)}</p>
                              ))}
                            </div>
                          )}
                          <button 
                            onClick={() => {
                              navigate(`/restaurant/${restaurant.slug || restaurant.id}`);
                            }}
                            className="bg-terracotta text-white text-sm px-4 py-2 rounded-full hover:bg-terracotta-dark transition-colors w-full"
                          >
                            Voir les plats
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
            <p className="text-center text-warmgray text-sm mt-4">
              Cliquez sur un marqueur pour voir les détails du restaurant
            </p>
          </div>
        </main>
      ) : (
        /* Menu V3 Warm Style - Contraste amélioré */
        <main className="relative z-10">
          <div className="container mx-auto px-4 pt-8 pb-3 relative z-10">
            <div className="max-w-2xl mx-auto">

            {filteredDishes.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-md border border-warmgray/20">
                <p className="text-warmgray-dark text-xl font-medium">
                  Menu non communiqué
                </p>
                <p className="text-warmgray text-sm mt-2">Aucun plat publié pour cette date.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {(() => {
                  // Pagination : toujours limiter à visibleCount
                  const displayedGroups = filteredDishes.slice(0, visibleCount);
                  const hasMore = filteredDishes.length > visibleCount;
                  const remaining = filteredDishes.length - visibleCount;

                  return (
                    <>
                      {displayedGroups.map((group, idx) => {
                        // Déterminer si on doit afficher le séparateur "Autres restaurants"
                        const showSeparator = group.isSecondarySection && 
                          (idx === 0 || !displayedGroups[idx - 1]?.isSecondarySection);
                  
                  return (
                    <React.Fragment key={group.restaurant?.id || idx}>
                      <RestaurantCard
                        group={group}
                        showSeparator={showSeparator}
                        favorites={favorites}
                        toggleFavorite={toggleFavorite}
                        events={events}
                        userLocation={userLocation}
                        formatDist={formatDist}
                        getCommune={getCommune}
                      />
                    </React.Fragment>
            )})}
                      {hasMore && (
                        <div className="flex justify-center py-4">
                          <button
                            onClick={() => setVisibleCount(prev => prev + 10)}
                            className="px-6 py-2.5 bg-white/90 text-[#4696C8] font-semibold rounded-full border-2 border-[#4696C8]/40 shadow-md hover:bg-[#4696C8] hover:text-white transition-all"
                            data-testid="show-more-restaurants"
                          >
                            Voir {remaining > 10 ? '10' : remaining} restaurant{remaining > 1 ? 's' : ''} de plus
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>
          )}
          </div>
          </div>
        </main>
      )}

      {/* Scroll buttons */}
      {showScrollButtons && (
        <div className="fixed bottom-20 right-6 flex flex-col gap-2 z-30">
          <button onClick={scrollToTop} className="bg-white/90 text-terracotta rounded-full h-10 w-10 border border-terracotta/20 shadow-md flex items-center justify-center hover:scale-105 hover:bg-terracotta hover:text-white transition-all">
            <ChevronUp className="h-5 w-5" />
          </button>
          <button onClick={scrollToBottom} className="bg-white/90 text-terracotta rounded-full h-10 w-10 border border-terracotta/20 shadow-md flex items-center justify-center hover:scale-105 hover:bg-terracotta hover:text-white transition-all">
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Popup nouveaux restaurants — rendu hors du <main> pour éviter le z-index bloquant */}
      {showNewRestaurantsPopup && newRestaurants.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowNewRestaurantsPopup(false)}
          data-testid="new-restaurants-popup-overlay"
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5"
            onClick={e => e.stopPropagation()}
            data-testid="new-restaurants-popup"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-stone-800 text-lg">Nouveaux restaurants</h3>
              <button 
                onClick={() => setShowNewRestaurantsPopup(false)}
                className="text-stone-400 hover:text-stone-600 text-xl font-bold"
                data-testid="new-restaurants-popup-close"
              >&times;</button>
            </div>
            <ul className="space-y-2">
              {newRestaurants.map(r => (
                <li key={r.id} className="flex items-center gap-2 py-1 border-b border-stone-100 last:border-0">
                  <span className="new-badge text-xs font-bold text-red-600">New</span>
                  <div>
                    <Link 
                      to={`/restaurant/${r.slug || r.id}`}
                      className="font-semibold text-stone-800 hover:text-amber-600 transition-colors"
                      onClick={() => setShowNewRestaurantsPopup(false)}
                    >
                      {r.name}
                    </Link>
                    <span className="text-stone-500 text-sm ml-1">— {r.commune}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Footer className="relative z-10 mt-8" />
    </div>
  );
};
